import React, { useState, useRef, useEffect } from "react";
import get from "lodash/get";
import { logout, updateUserProfile, signImage, uploadS3ToTemp } from "@quintype/bridgekeeper-js";
import { AccessType } from "@quintype/components";
import { googleLogout } from "@react-oauth/google";
import { useDispatch, useSelector } from "react-redux";
import { object, func } from "prop-types";
import PeopleIcon from "../../../atoms/PeopleIcon";
import EditIcon from "../../../atoms/EditIcon";
import Button from "../../../atoms/Button";
import { SvgIconHandler } from "../../../../components/atoms/SvgIconHandler";

import { MEMBER_UPDATED } from "../../../store/actions";
import Exit from "../../../atoms/Exit";
import { navigateTo } from "../../../../arrow/utils/utils";
import LoadingIndicatorComponent from "../../../atoms/loading-indicator";
import ManageSubscription from "./ManageSubscription";

import "./my-account.m.css";

let tempImageKey = null;

const MyAccount = ({ member = {} }) => {
  const imgUrl = get(member, ["avatar-url"], null);
  const email = get(member, ["email"], "");
  const MemberName = get(member, ["name"], "");
  const [loading, setLoading] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [name, setName] = useState(MemberName);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const phone = get(member, ["metadata", "phone-number"], "");
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const activeTab = pathname === "/profile/subscription" ? "subscription" : "profile";

  const { key, accessTypeBkIntegrationId } = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "accesstypeConfig"], {})
  );

  const enableSubscription = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "enable_subscription"], true)
  );

  useEffect(() => {
    // Don't redirect immediately when member is null
    // Wait for the member data to load from the server
    const timeoutId = setTimeout(() => {
      setHasCheckedAuth(true);

      // Only redirect if member is still null after the timeout
      // This means the user is not logged in
      if (member === null) {
        console.log("Member is still null after timeout, redirecting to home page");
        navigateTo(dispatch, "/");
      }
    }, 3000); // 3 seconds should be enough for member data to load

    return () => clearTimeout(timeoutId);
  }, [member, dispatch]);

  // Show loading state while checking authentication
  if (!hasCheckedAuth) {
    return (
      <div styleName="container">
        <div styleName="loading-container">
          <LoadingIndicatorComponent />
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  // If member is null after checking, don't render the profile content
  if (member === null) {
    return null; // This will trigger the redirect in the useEffect
  }

  const profileUrl = tempImageUrl || imgUrl;

  const prepareFormData = (keys, imageList, res) => {
    // eslint-disable-next-line no-undef
    const formData = new FormData();

    keys.forEach((key) => {
      key === "file" ? formData.append(key, imageList[0]) : formData.append(key, res[key]);
    });

    return formData;
  };

  const onProfileChange = async (event) => {
    setLoading(true);
    const imageList = event.target.files;
    const keys = [
      "key",
      "Content-Type",
      "policy",
      "acl",
      "success_action_status",
      "AWSAccessKeyId",
      "signature",
      "file",
    ];

    try {
      const signedImage = await signImage(imageList[0].name, imageList[0].type);
      const formData = prepareFormData(keys, imageList, signedImage);
      const uploadedImage = await uploadS3ToTemp(signedImage.action, formData);
      if (uploadedImage.status === 201) {
        setTempImageUrl(URL.createObjectURL(imageList[0]));
        tempImageKey = signedImage.key;
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const logoutHandler = async () => {
    setLoading(true);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_logout",
      user_id: get(member, ["id"], "NA"),
      user_email: get(member, ["email"], "NA"),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });

    logout()
      .then(() => {
        dispatch({
          type: MEMBER_UPDATED,
          member: null,
        });
        googleLogout();
        navigateTo(dispatch, "/");
        window.location.href = window.location.origin + "/";
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    const body = { name, email: member.email || member["phone-number"] };

    if (tempImageKey !== null) {
      body["temp-s3-key"] = tempImageKey;
    }

    try {
      setLoading(true);
      const currentUserResp = await updateUserProfile(body);
      dispatch({ type: MEMBER_UPDATED, member: get(currentUserResp, ["user"], null) });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }

    setIsEditing(!isEditing);
    setTempImageUrl(null);
  };

  const handleDiscardChanges = () => {
    setName(MemberName);
    setIsEditing(!isEditing);
    setTempImageUrl(null);
  };

  const showFileUploadview = () => {
    fileInputRef.current.click();
    setIsEditing(true);
  };

  // Scoped ProfileDetails component with access to all state and functions
  const ProfileDetails = () => (
    <div styleName="profileContainer">
      <h2 styleName="title">Profile Details</h2>

      <div styleName="profileCard">
        {/* Profile Image */}
        <div styleName="profileImageContainer">
          <div styleName="profileImage">{profileUrl ? <img alt={name} src={`${profileUrl}`} /> : <PeopleIcon />}</div>
          <button styleName="editImageButton" onClick={() => showFileUploadview()}>
            <EditIcon />
          </button>
        </div>

        {/* Profile Info */}
        <div styleName="profileInfo">
          <input
            type="file"
            ref={fileInputRef}
            onChange={onProfileChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          <label styleName="label">Name</label>
          <div styleName="inputContainer">
            <input
              type="text"
              value={name || member?.name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isEditing}
              styleName="input"
            />
            <button styleName="editButton" onClick={() => setIsEditing(true)}>
              <EditIcon />
            </button>
          </div>

          <label styleName="label">Email</label>
          <input type="email" value={email} disabled styleName="input" />

          {/* <label styleName="label">Phone Number</label>
        <input type="text" value={phone} disabled styleName="input" /> */}
        </div>
        {isEditing ? (
          <div styleName="button-wrapper">
            <Button customStyleName="bg-brand-primary" onClickFunction={handleSaveChanges}>
              Save Changes
            </Button>
            <Button customStyleName="story-header-btn" onClickFunction={handleDiscardChanges}>
              Discard Changes
            </Button>
          </div>
        ) : null}
      </div>
      <LoadingIndicatorComponent loading={loading} />
    </div>
  );

  return (
    <div styleName="container">
      <div styleName="titile-wrapper">
        <h1 styleName="my-account">My Account</h1>
        <button styleName="logout" onClick={logoutHandler}>
          <Exit /> Log out
        </button>
      </div>

      <div styleName="tabs">
        <button
          styleName={`${activeTab === "profile" ? "active-profile" : ""}`}
          onClick={() => {
            navigateTo(dispatch, "/profile");
          }}
        >
          <SvgIconHandler type="profile" width="18" height="18" viewBox="0 0 18 18" /> Profile Details
        </button>
        {enableSubscription && (
          <button
            styleName={`${activeTab === "subscription" ? "active-sub" : ""}`}
            onClick={() => {
              navigateTo(dispatch, "/profile/subscription");
            }}
          >
            <SvgIconHandler type="crown" width="18" height="18" viewBox="0 0 18 18" /> Manage Subscription
          </button>
        )}
      </div>

      {!enableSubscription ? (
        <ProfileDetails />
      ) : activeTab === "profile" ? (
        <ProfileDetails />
      ) : (
        <AccessType
          enableAccesstype={true}
          accessTypeKey={key}
          email={email}
          phone={phone}
          accessTypeBkIntegrationId={accessTypeBkIntegrationId}
        >
          {({ initAccessType, getSubscriptionForUser, cancelSubscription }) => (
            <ManageSubscription
              member={member}
              getSubscriptionForUser={getSubscriptionForUser}
              cancelSubscription={cancelSubscription}
              initAccessType={initAccessType}
            />
          )}
        </AccessType>
      )}
    </div>
  );
};

MyAccount.propTypes = {
  member: object,
  getSubscriptionForUser: func,
  cancelSubscription: func,
  initAccessType: func,
  isATGlobal: object,
};

export default MyAccount;
